import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMediaQuery } from 'hooks/useMediaQuery'
import { LatLngTuple } from 'leaflet'
import { debounce } from 'lodash'
import { useRecoilState } from 'recoil'
import { floorDetailState } from 'store/buildingStore'
import {
  currentAnnotation,
  currentPovState,
  povPerPointState
} from 'store/krpanoStore'
import currentViewerState from 'store/viewerStore'

interface UseFloorMapProps {
  initialZoomOut?: boolean
  setSelectedPointID: Dispatch<SetStateAction<string>>
  points: any[]
}

export default function useFloorMap({
  initialZoomOut = false,
  setSelectedPointID,
  points
}: UseFloorMapProps) {
  const [currentViewer, setCurrentViewer] = useRecoilState(currentViewerState)
  const [, setPovPerPoint] = useRecoilState(povPerPointState)
  const [, setCurrentSelected] = useRecoilState(currentAnnotation)
  const [, setCurrentPov] = useRecoilState(currentPovState)
  const [, setSearchParams] = useSearchParams()

  // State management
  const [isZoomOutMap, setIsZoomOutMap] = useState(initialZoomOut)
  const [imgBounds, setImgBounds] = useState<LatLngTuple>([1000, 1000])
  const [isMapLoading, setIsMapLoading] = useState(false)
  const [showMap, setShowMap] = useState(true)
  const [isZoomTransition, setIsZoomTransition] = useState(false)

  const isFloorChange = useRef<any>(false)
  const povMarkerRef = useRef<any>(null)
  const [floorDetail, setFloorDetail] = useRecoilState(floorDetailState)
  const [zoomValue, setZoomValue] = useState({
    zoom: 0,
    minZoom: 0,
    maxZoom: 5
  })
  // state for is SP screen
  const matches = useMediaQuery('(min-width: 768px)')
  const isSmartphone = useMediaQuery('(max-width: 1024px)')
  // Refs
  const mapContainerRef = useRef<any>(null)
  const mapInitializedRef = useRef<boolean>(false)
  const zoomTimerRef = useRef<NodeJS.Timeout | null>(null)
  const isFittingBoundsRef = useRef<boolean>(false)
  const isZoomingToPointRef = useRef<boolean>(false)

  // Calculate map size style
  const getMapSizeInlineStyle = useCallback(() => {
    const baseStyles: any = {
      willChange: 'transform, width, height',
      padding: 0,
      boxSizing: 'border-box' as const,
      overflow: 'hidden' as const,
      display: 'block' as const,
      opacity: isMapLoading ? 0.1 : 1
    }

    if (isZoomTransition) {
      baseStyles.transition =
        'opacity 400ms ease-in-out, transform 600ms ease-in-out, height 600ms ease'
    } else {
      baseStyles.transition = 'none'
    }

    const originalHeight = imgBounds[0] || 1
    const originalWidth = imgBounds[1] || 1
    const ratio = originalWidth / originalHeight
    const windowWidth = window.innerWidth
    const windowHeight = window.innerHeight

    // If map is minimized (not zoomed out)
    if (!isZoomOutMap) {
      let targetWidth = 0
      let targetHeight = 0

      if (isSmartphone) {
        // SP: map width = 30% of screen width, height = 20% of screen height
        targetWidth = Math.round(windowWidth * 0.3)
        const maxHeight = Math.round(windowHeight * 0.2)
        targetHeight = Math.min(Math.round(targetWidth / ratio), maxHeight)
      } else {
        // PC: map width = 10% of screen width
        targetWidth = Math.round(windowWidth * 0.1)
        targetHeight = Math.round(targetWidth / ratio)
      }

      return {
        ...baseStyles,
        width: `${targetWidth}px`,
        height: `${targetHeight}px`
      }
    }

    // If map is maximized (zoomed out)
    if (isZoomOutMap) {
      let targetWidth = 0
      let targetHeight = 0

      if (isSmartphone) {
        // SP zoom-out: cap width at 80% to keep usable view
        targetWidth = Math.round(windowWidth * 0.8)
        targetHeight = Math.round(targetWidth / ratio)
      } else {
        // PC zoom-out: height = 70% of screen height
        targetHeight = Math.round(windowHeight * 0.7)
        targetWidth = Math.round(targetHeight * ratio)
      }

      return {
        ...baseStyles,
        width: `${targetWidth}px`,
        height: `${targetHeight}px`
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isZoomTransition,
    isZoomOutMap,
    isMapLoading,
    imgBounds,
    matches,
    isSmartphone
  ])
  // Handle smooth transition
  const animateMapChange = useCallback(
    (callback: () => void) => {
      // Clear any existing zoom timers
      if (zoomTimerRef?.current) {
        clearTimeout(zoomTimerRef?.current)
      }

      setIsMapLoading(true)
      setShowMap(false)

      // Delay to prepare for animation
      zoomTimerRef.current = setTimeout(() => {
        // Execute callback (like changing zoom state)
        callback()

        // Delay to let DOM update with new styles
        zoomTimerRef.current = setTimeout(() => {
          if (mapContainerRef?.current) {
            // Redraw map with new size
            mapContainerRef?.current.invalidateSize({ animate: false })

            // Fit bounds to ensure content is properly displayed
            if (imgBounds[0] > 0 && imgBounds[1] > 0) {
              const bounds = [
                [0, 0],
                [imgBounds[0], imgBounds[1]]
              ]

              mapContainerRef?.current.setMinZoom(-999)
              mapContainerRef?.current.fitBounds(bounds, {
                animate: false,
                duration: 0,
                easeLinearity: 0.5,
                padding: [0, 0]
              })
              mapContainerRef?.current.setMaxBounds(bounds)

              // Set zoom levels
              const minZoomLevel =
                mapContainerRef?.current.getBoundsZoom(bounds)
              mapContainerRef?.current.setView(
                [imgBounds[0] / 2, imgBounds[1] / 2],
                minZoomLevel,
                {
                  animate: false
                }
              )
              // Set zoom constraints
              mapContainerRef?.current.setMinZoom(minZoomLevel)
              mapContainerRef?.current.setMaxZoom(minZoomLevel + 5)
              const newZoomValue = {
                zoom: minZoomLevel,
                minZoom: minZoomLevel,
                maxZoom: minZoomLevel + 5
              }
              setZoomValue(newZoomValue)

              // Force another invalidate after bounds are set
              requestAnimationFrame(() => {
                if (mapContainerRef?.current) {
                  mapContainerRef?.current.invalidateSize({ animate: false })
                }
              })
            }
          }

          // Show map with a delay
          setShowMap(true)

          // Stop loading state after animation completes
          zoomTimerRef.current = setTimeout(() => {
            setIsMapLoading(false)
            // Final invalidate to ensure correct size
            if (mapContainerRef?.current) {
              requestAnimationFrame(() => {
                mapContainerRef?.current.invalidateSize({ animate: true })
              })
            }
          }, 1000)
        }, 1000)
      }, 1000)
    },
    [imgBounds]
  )

  // Toggle zoom
  const handleZoomToggle = useCallback(() => {
    setIsZoomTransition(true)

    animateMapChange(() => {
      setIsZoomOutMap((prev) => {
        return !prev
      })
    })

    // Reset zoom transition flag after animation completes
    setTimeout(() => {
      setIsZoomTransition(false)
    }, 1500)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animateMapChange, isZoomOutMap])

  // Initialize map
  const initializeMap = useCallback(() => {
    if (
      !floorDetail?.mapFile?.s3Path ||
      !mapContainerRef?.current ||
      mapInitializedRef.current
    )
      return

    setShowMap(false)
    setIsMapLoading(true)
    mapInitializedRef.current = true

    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.src = floorDetail?.mapFile?.s3Path

    img.onload = () => {
      const { width, height } = img
      setImgBounds([height, width])

      // Force invalidation after layout update and then show the map
      setTimeout(() => {
        if (mapContainerRef?.current) {
          mapContainerRef.current.setMinZoom(-10)

          // Invalidate size and fit bounds again to ensure the image fully fills the container
          const bounds = [
            [0, 0],
            [height, width]
          ]
          mapContainerRef?.current.invalidateSize({ animate: false })
          mapContainerRef?.current.fitBounds(bounds, {
            animate: false,
            padding: [0, 0]
          })
          mapContainerRef?.current.setMaxBounds(bounds)
        }
        setShowMap(true)
        setIsMapLoading(false)
        setTimeout(() => {
          if (mapContainerRef?.current) {
            const bounds = [
              [0, 0],
              [height, width]
            ]

            const minZoomLevel = mapContainerRef.current.getBoundsZoom(bounds)
            mapContainerRef.current.setMinZoom(minZoomLevel)
            mapContainerRef.current.setMaxZoom(minZoomLevel + 5)
            mapContainerRef.current.setMaxBounds(bounds)

            const newZoomValue = {
              zoom: minZoomLevel,
              minZoom: minZoomLevel,
              maxZoom: minZoomLevel + 5
            }
            setZoomValue(newZoomValue)
          }
        }, 600)
      }, 500)
    }

    img.onerror = () => {
      setImgBounds([1000, 1000])
      setIsMapLoading(false)
      mapInitializedRef.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [floorDetail?.mapFile?.s3Path])

  // Reset map when floor changes
  const resetMap = useCallback(() => {
    mapInitializedRef.current = false
    setIsMapLoading(true)
    setShowMap(false)
    setImgBounds([1000, 1000])
    setIsZoomTransition(false)
    setZoomValue({
      zoom: 5,
      minZoom: 0,
      maxZoom: 5
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Zoom handlers - allow rapid clicks without throttle
  const handleZoomIn = useCallback(() => {
    if (!mapContainerRef?.current) return

    const zoom = mapContainerRef.current.getZoom()
    const maxZoom = mapContainerRef.current.getMaxZoom()

    if (zoom < maxZoom) {
      // Use shorter duration for faster response, Leaflet will queue animations
      mapContainerRef.current.zoomIn(1, {
        animate: true,
        duration: 0.2,
        easeLinearity: 0.5
      })

      const newZoom = Math.min(zoom + 1, maxZoom)
      setZoomValue({
        zoom: newZoom,
        minZoom: mapContainerRef.current.getMinZoom(),
        maxZoom
      })
    }
  }, [])

  const handleZoomOut = useCallback(() => {
    if (!mapContainerRef?.current) return

    const zoom = mapContainerRef.current.getZoom()
    const minZoom = mapContainerRef.current.getMinZoom()

    if (zoom > minZoom) {
      // Use shorter duration for faster response, Leaflet will queue animations
      mapContainerRef.current.zoomOut(1, {
        animate: true,
        duration: 0.2,
        easeLinearity: 0.5
      })

      const newZoom = Math.max(zoom - 1, minZoom)
      setZoomValue({
        zoom: newZoom,
        minZoom,
        maxZoom: mapContainerRef.current.getMaxZoom()
      })
    }
  }, [])

  const handleZoomToPoint = useCallback((x: number, y: number) => {
    if (!mapContainerRef?.current) return

    // Prevent rapid clicks
    if (isZoomingToPointRef.current) {
      return
    }

    const map = mapContainerRef.current
    const zoom = map.getZoom()
    const maxZoom = map.getMaxZoom()
    const nextZoom = zoom < maxZoom ? zoom + 1 : zoom

    // Mark as zooming and disable dragging
    isZoomingToPointRef.current = true
    map.dragging.disable()

    // Use shorter duration for faster response and less chance of drag interference
    map.setView([y, x], nextZoom, {
      animate: true,
      duration: 0.25,
      easeLinearity: 0.5
    })

    // Re-enable dragging after animation (250ms + buffer)
    setTimeout(() => {
      if (mapContainerRef?.current) {
        mapContainerRef.current.dragging.enable()
        isZoomingToPointRef.current = false
      }
    }, 300)
  }, [])

  const handleResetZoom = useCallback(() => {
    if (!mapContainerRef?.current) return

    const map = mapContainerRef.current
    const minZoom = map.getMinZoom()
    const bounds = map.getBounds()
    const center = bounds.getCenter()

    // Reset to minimum zoom level and center the map
    map.setView(center, minZoom, {
      animate: true,
      duration: 0.3,
      easeLinearity: 0.5
    })

    // Update zoom state
    setZoomValue({
      zoom: minZoom,
      minZoom,
      maxZoom: map.getMaxZoom()
    })
  }, [])

  // Get the inline style for the floor select container that matches the map size
  const getFloorSelectInlineStyle = useCallback(() => {
    // Floor list width:
    //   PC: 10% of screen width
    //   SP: 30% of screen width
    const windowWidth = window.innerWidth

    let width: number
    if (isSmartphone) {
      // SP: 30% of total screen width
      width = Math.round(windowWidth * 0.3)
    } else {
      // PC: 10% of total screen width
      width = Math.round(windowWidth * 0.1)
    }

    return {
      backgroundColor: '#5F9EA0',
      width: `${width}px`,
      height: '40px',
      padding: '0rem',
      fontSize: matches ? '14px' : '12px'
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matches, isSmartphone])

  // Handle floor selection
  const handleSelectFloor = (value: number) => {
    isFloorChange.current = true
    // Reset states
    resetMap()

    // Update URL
    setSearchParams((params) => {
      params.set('floor', String(value))
      params.delete('point')
      params.delete('area')
      params.delete('subimage')
      params.delete('hlookat')
      params.delete('vlookat')
      params.delete('fov')
      return params
    })

    // Update selected floor
    setCurrentViewer((prev) => ({
      ...prev,
      selectedArea: undefined,
      selectedPoint: undefined,
      selectedFloor: value,
      isClickToNextFloor: false,
      isSelectPointInLeftMenu: true,
      isClickFromTourCarousel: false
    }))
  }

  // Handle marker click
  const handleMarkerClick = (point: any) => {
    // Snapshot POV of the point we are leaving so it can be restored on return
    if (
      currentViewer.pointDetail?.id &&
      currentViewer.pointDetail.id !== point.id
    ) {
      const embedpano = document.getElementById('embedpano-viewer') as any
      if (embedpano) {
        const h = Number(embedpano.get('view.hlookat'))
        const v = Number(embedpano.get('view.vlookat'))
        const z = Number(embedpano.get('view.fov'))
        if (!Number.isNaN(h) && !Number.isNaN(v) && !Number.isNaN(z)) {
          const leavingId = currentViewer.pointDetail.id
          setPovPerPoint((prev) => ({ ...prev, [leavingId]: { h, v, z } }))
        }
      }
    }
    // Reset currentPov so the viewer uses the point's default initView
    setCurrentPov(null)
    // Also clear the stored POV for the target point so the viewer falls through
    // to initView instead of reusing the last camera angle from povPerPoint
    setPovPerPoint((prev) => {
      const next = { ...prev }
      delete next[point.id]
      return next
    })
    setCurrentSelected(undefined)

    // Update URL
    const urlToUpdate = new URL(window.location.href)
    urlToUpdate.searchParams.set('point', point.id)
    urlToUpdate.searchParams.set('area', String(point.areaId))
    urlToUpdate.searchParams.delete('subimage')
    window.history.pushState({}, '', urlToUpdate.toString())

    setSelectedPointID(point.id)
    setCurrentViewer((prev) => ({
      ...prev,
      selectedArea: `${point.areaId}`,
      selectedPoint: point.id,
      selectedImage: point.image360?.id,
      // Map click should reset POV to default (treat like left-menu selection)
      isSelectPointInLeftMenu: true,
      isClickToDirection: false,
      isClickFromTourCarousel: false
    }))
  }

  // Handle window resize
  useEffect(() => {
    const handleResize = debounce(() => {
      if (mapContainerRef?.current) {
        mapContainerRef?.current.invalidateSize({ animate: true })

        if (imgBounds[0] > 0 && imgBounds[1] > 0) {
          const bounds = [
            [0, 0],
            [imgBounds[0], imgBounds[1]]
          ]

          mapContainerRef?.current.fitBounds(bounds, {
            animate: true,
            duration: 0.7,
            padding: [0, 0]
          })
        }
      }
    }, 200)

    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)

      // Clean up any pending timers
      if (zoomTimerRef.current) {
        clearTimeout(zoomTimerRef.current)
      }
    }
  }, [imgBounds])

  // Handle zoom state changes
  useEffect(() => {
    if (
      mapContainerRef?.current &&
      showMap &&
      !isMapLoading &&
      !isFittingBoundsRef.current
    ) {
      isFittingBoundsRef.current = true
      const timeoutId = setTimeout(() => {
        if (!mapContainerRef?.current) {
          isFittingBoundsRef.current = false
          return
        }

        mapContainerRef.current.invalidateSize({ animate: true })

        if (imgBounds[0] > 0 && imgBounds[1] > 0) {
          const bounds = [
            [0, 0],
            [imgBounds[0], imgBounds[1]]
          ]

          mapContainerRef.current.fitBounds(bounds, {
            animate: true,
            duration: 0.7,
            padding: [0, 0]
          })

          // Reset flag after animation completes
          setTimeout(() => {
            isFittingBoundsRef.current = false
          }, 800)
        } else {
          isFittingBoundsRef.current = false
        }
      }, 200)

      return () => {
        clearTimeout(timeoutId)
        isFittingBoundsRef.current = false
      }
    }
  }, [isZoomOutMap, showMap, isMapLoading, imgBounds])

  // Add zoom event listener - update state after animation completes
  useEffect(() => {
    const map = mapContainerRef?.current
    if (!map) return

    const handleZoomEnd = () => {
      // Skip update if we're currently fitting bounds to avoid infinite loop
      if (isFittingBoundsRef.current) return

      // Update zoom value after animation completes
      const currentZoom = map.getZoom()
      const minZoom = map.getMinZoom()
      const maxZoom = map.getMaxZoom()

      setZoomValue({
        zoom: currentZoom,
        minZoom,
        maxZoom
      })
    }

    // Listen to zoomend to get final value after animation
    map.on('zoomend', handleZoomEnd)
    // Only listen to moveend for user interactions, not programmatic fitBounds
    const handleMoveEnd = () => {
      if (!isFittingBoundsRef.current) {
        handleZoomEnd()
      }
    }
    map.on('moveend', handleMoveEnd)

    // Clean up the event listener on unmount or when dependencies change
    return () => {
      map.off('zoomend', handleZoomEnd)
      map.off('moveend', handleMoveEnd)
    }
  }, [showMap, isMapLoading, floorDetail?.id])

  // Initialize map when URL changes
  useEffect(() => {
    if (floorDetail?.mapFile?.s3Path) {
      resetMap()
      // Ensure mapInitializedRef is false right before calling initializeMap
      // to prevent skipping due to timing conflicts with multiple resetMap calls
      setTimeout(() => {
        mapInitializedRef.current = false
        initializeMap()
      }, 300)
    }
  }, [floorDetail?.mapFile?.s3Path, initializeMap, resetMap])

  useEffect(() => {
    if (floorDetail && !floorDetail?.mapFile?.s3Path) {
      // Ensure map loading state is cleared so parent can proceed
      setIsMapLoading(false)
      setShowMap(false)
      setImgBounds([1000, 1000])
      mapInitializedRef.current = false
    }
  }, [floorDetail])

  // effect set default point when select floor from floor select in right  screen of tour viewer
  useEffect(() => {
    if (isFloorChange.current) {
      isFloorChange.current = false
      const defaultPoint = [...(points || [])].sort(
        (a, b) => a.order - b.order
      )[0]

      setCurrentViewer((prev) => ({
        ...prev,
        selectedPoint: defaultPoint?.id,
        selectedArea: `${defaultPoint?.areaId}`,
        selectedImage: defaultPoint?.image360?.id
      }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points])

  return {
    showMap,
    imgBounds,
    isZoomOutMap,
    isMapLoading,
    currentViewer,
    mapContainerRef,
    resetMap,
    handleZoomIn,
    handleZoomOut,
    animateMapChange,
    handleZoomToggle,
    handleZoomToPoint,
    handleResetZoom,
    handleSelectFloor,
    setCurrentViewer,
    handleMarkerClick,
    getMapSizeInlineStyle,
    getFloorSelectInlineStyle,
    povMarkerRef,
    floorDetail,
    setFloorDetail,
    zoomValue
  }
}
